import { UntypedFormGroup } from '@angular/forms';
import { filter, switchMap, take, tap } from 'rxjs/operators';
import { LoanStatus, LockStatusType } from '../../../cmg-1003/models/cmg-1003-common.enum';
import { SessionModel } from './session.models';
import { Cmg1003StoreQueryService } from '../../../loan-state';
import { SessionQuery } from './session.query';
import { combineLatest, of } from 'rxjs';
import { SecurityProfileRole } from '../../../loan-state/security-profile';

/* Statuses that only lock certain fields when the RateLockStatus is Locked */
export const softLockStatuses = [
  LoanStatus.Prospect,
  LoanStatus.CreditOnly,
  LoanStatus.PreApproved,
  LoanStatus.LePending,
  LoanStatus.Disclosed,
  LoanStatus.Sandbox,
];
export const isLoanSaveAllowed = (loanStatusId: number) => softLockStatuses.some((slStatusId) => slStatusId === loanStatusId);

/** PA profiles restricted from editing Borrower Info in non-allowed statuses (ADO #35105) */
export const paBorrowerInfoRestrictedProfiles = new Set<string>([
  SecurityProfileRole.ProductionAssistant,
  SecurityProfileRole.JVProductionAssistant,
  SecurityProfileRole.DisclosurePA,
  SecurityProfileRole.SuperDisclosurePA,
  SecurityProfileRole.PA2,
  SecurityProfileRole.PA2LE,
]);

/** Statuses where PA profiles CAN edit Borrower Info */
export const paBorrowerInfoAllowedStatuses = new Set<LoanStatus>([
  LoanStatus.Prospect,
  LoanStatus.CreditOnly,
  LoanStatus.LePending,
  LoanStatus.Disclosed,
  LoanStatus.Sandbox,
  LoanStatus.FileReceived,
]);

/** Check if a PA profile is restricted from editing Borrower Info for the given status */
export const isPaBorrowerInfoRestricted = (profileName: string, loanStatusId: LoanStatus): boolean => {
  if (!profileName || loanStatusId === null || loanStatusId === undefined) {
    return false;
  }
  return paBorrowerInfoRestrictedProfiles.has(profileName) && !paBorrowerInfoAllowedStatuses.has(loanStatusId);
};

/* Statuses that occur before “File Received” */
export const preFileReceivedLoanStatuses = new Set<LoanStatus>([
  LoanStatus.Prospect,
  LoanStatus.CreditOnly,
  LoanStatus.PreApproved,
  LoanStatus.LePending,
  LoanStatus.Disclosed,
  LoanStatus.Sandbox,
]);

/**
 * Fully disables form(s) if needed, based on LoanStatus or RateLockStatus.
 * @param formGroups FormGroups to disable.
 * @param storeQuery An instance of Cmg1003StoreQueryService.
 * @param callbackFn Optional. Any additional function(s) to execute, if full disabling of form(s) is NOT needed.
 */
export const disableFormsBasedOnStatus = (
  formGroups: UntypedFormGroup[],
  storeQuery: Cmg1003StoreQueryService,
  options?: {
    ignoreSyncType?: boolean;
    controlsToPreserve?: string[];
  },
) => {
  combineLatest([storeQuery.isLockedDown$, storeQuery.statusRequiresFullDisabling$(options?.ignoreSyncType)])
    .pipe(
      take(1),
      tap(([isLockedDown, statusRequiresFullDisabling]) => {
        if (isLockedDown || statusRequiresFullDisabling) {
          formGroups.forEach((fg) => {
            fg.disable({ emitEvent: false });

            if (!isLockedDown && options?.controlsToPreserve?.length) {
              options.controlsToPreserve.forEach((controlName) => {
                if (fg.get(controlName)) {
                  fg.get(controlName).enable({ emitEvent: false });
                }
              });
            }
          });
        }
      }),
    )
    .subscribe();
};

// This is the original disableFormControlsBasedOnStatus method that existed before it was refactored for 13852,
// and is only needed for PropertyAndGiftsInformationComponent.
// TODO refactor PropertyAndGiftsInformationComponent to use new version and remove this old version.
export const disableFormControlsBasedOnStatusOriginal = (
  formGroups: UntypedFormGroup[],
  sessionQuery: SessionQuery,
  storeQuery: Cmg1003StoreQueryService,
  forceDisableOnLockKeys?: string[],
  alwaysEnabledKeys?: string[],
  callbackFn?: (session?: SessionModel) => void,
) => {
  combineLatest([storeQuery.isLockedDown$, sessionQuery.select()])
    .pipe(
      filter(([isLockedDown, session]) => !!session.loanStatusId && session.all1003Loaded && isLockedDown !== undefined),
      take(1),
      tap(([isLockedDown, session]) => {
        if (isLockedDown || session.rateLockStatus === LockStatusType.Locked || !softLockStatuses.includes(session.loanStatusId)) {
          formGroups.forEach((fg) => {
            Object.keys(fg.controls).forEach((key) => {
              const ctrl = fg?.get(key);
              if (
                (isLockedDown || !softLockStatuses.includes(session.loanStatusId) || forceDisableOnLockKeys?.includes(key)) &&
                !alwaysEnabledKeys?.includes(key)
              ) {
                ctrl.disable({ emitEvent: false });
              }
            });
          });
        }

        if (callbackFn) {
          callbackFn(session);
        }
      }),
    )
    .subscribe();
};

/**
 * Fully disables form(s) if needed, based on LoanStatus or RateLockStatus.
 * @param formGroups FormGroups to disable.
 * @param storeQuery An instance of Cmg1003StoreQueryService.
 * @param forceDisableOnLockKeys Controls keys that should be disabled regardless of soft lock statuses.
 * @param alwaysEnabledKeys Controls keys that should always be enabled.
 * @param callbackFn Optional. Any additional function(s) to execute, if full disabling of form(s) is NOT needed.
 * @param formDisabled Optional. Used to prevent unnecessary statusRequiresFullDisabling checks.
 */
export const disableFormControlsBasedOnStatus = (
  formGroups: UntypedFormGroup[],
  storeQuery: Cmg1003StoreQueryService,
  forceDisableOnLockKeys?: string[],
  alwaysEnabledKeys?: string[],
  callbackFn?: (session?: SessionModel) => void,
  formDisabled?: boolean,
  ignoreSyncType?: boolean,
) => {
  storeQuery.isLockedDown$
    .pipe(
      switchMap((isLocked) => {
        if (isLocked) {
          _disableFormControlsBasedOnStatus(true, formGroups, null, null, callbackFn);
          return of(null);
        } else {
          if (formDisabled !== null && formDisabled !== undefined) {
            _disableFormControlsBasedOnStatus(formDisabled, formGroups, forceDisableOnLockKeys, alwaysEnabledKeys, callbackFn);
            return of(null);
          } else {
            return storeQuery.statusRequiresFullDisabling$(ignoreSyncType).pipe(
              tap((statusRequiresFullDisabling) => {
                _disableFormControlsBasedOnStatus(statusRequiresFullDisabling, formGroups, forceDisableOnLockKeys, alwaysEnabledKeys, callbackFn);
              }),
            );
          }
        }
      }),
      take(1),
    )
    .subscribe();
};

const _disableFormControlsBasedOnStatus = (
  statusRequiresFullDisabling: boolean,
  formGroups: UntypedFormGroup[],
  forceDisableOnLockKeys?: string[],
  alwaysEnabledKeys?: string[],
  callbackFn?: (session?: SessionModel) => void,
) => {
  formGroups.forEach((fg) => {
    Object.keys(fg.controls).forEach((key) => {
      const ctrl = fg?.get(key);

      if ((statusRequiresFullDisabling || forceDisableOnLockKeys?.includes(key)) && !alwaysEnabledKeys?.includes(key)) {
        ctrl.disable({ emitEvent: false });
      }
    });
  });

  if (callbackFn) {
    callbackFn();
  }
};
